import { Layer, Source } from "@vis.gl/react-maplibre";
import { FC } from "react";

import { MapLibreRouteArrows } from "./maplibre-route-arrows";

interface IMapLibreGeoJsonLineProps {
  id: string;
  feature: GeoJSON.Feature<GeoJSON.LineString | GeoJSON.MultiLineString>;
  color: string;
  opacity?: number;
  lineWidth?: number;
  dashArray?: number[];
  /**
   * Perpendicular shift (screen px, positive = right of travel direction) applied by MapLibre on the GPU.
   * Prefer this over displacing coordinates: a pixel distance baked into lat/lng is only correct at the zoom
   * it was computed at, and becomes kilometres wide when that zoom was low.
   */
  lineOffsetPx?: number;
  /** Repeating direction arrows along the line (MapLibre twin of leaflet-textpath "►"). */
  withArrows?: boolean;
  /**
   * Gap (px) between those arrows. Pass a value distinct from the default on a line that runs alongside
   * another arrowed line, so the two rows of arrows do not land on top of each other.
   */
  arrowSpacingPx?: number;
  /** Below this zoom the arrows are skipped entirely, symbol layout included. Unset = always drawn. */
  arrowsMinZoom?: number;
}

export const MapLibreGeoJsonLine: FC<IMapLibreGeoJsonLineProps> = ({
  id,
  feature,
  color,
  opacity = 1,
  lineWidth = 4,
  dashArray,
  lineOffsetPx,
  withArrows = false,
  arrowSpacingPx,
  arrowsMinZoom,
}) => (
  <Source id={id} type="geojson" data={feature}>
    <Layer
      id={`${id}-line`}
      type="line"
      paint={{
        ["line-color"]: color,
        ["line-width"]: lineWidth,
        ["line-opacity"]: opacity,
        ...(dashArray ? { ["line-dasharray"]: dashArray } : {}),
        ...(lineOffsetPx ? { ["line-offset"]: lineOffsetPx } : {}),
      }}
    />

    {/* Inside the source, not beside it: the arrows share this line's geometry instead of indexing a copy. */}
    {withArrows && (
      <MapLibreRouteArrows
        color={color}
        offsetPx={lineOffsetPx}
        spacingPx={arrowSpacingPx}
        minZoom={arrowsMinZoom}
      />
    )}
  </Source>
);
